import React, {
	useMemo, useState, useRef, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { ColorResult } from '@uiw/react-color';
import classNames from 'classnames';
import ImagePicker from './image-picker';
import { UploadImage, UploadResponse } from '../../../connection/upload-image';
import {
	GetUserMediaCanvasBackgrounds,
	UploadUserMediaCanvasBackground,
	DeleteUserMedia,
} from '../../../connection/media';
import type { ImagePickerMedia, UserMedia } from '../../../stores/types/media';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import {
	getChannelLogoFromMeeting,
	getFullAssetURI,
} from '../../../utils/utils';
import WaitingIndicator from '../../general-components/waiting-indicator';
import { GreenroomState } from '../../../stores/types/types';
import type { AppSettings } from '../../../stores/types/settings';
import InfoBadge, { InfoBadgeMsg } from '../../general-components/tooltips/info-badge';
import { IMG_TYPES } from '../../../types/constants';
import { getFileExtension, resizeImage } from '../../../utils/media';
import ColorPicker from './color-pickers/color-picker';

interface Props {
	settingsForShotEdited: (newQueue: any) => void;
	appliedMatchesQueue: boolean;
	queue: AppSettings
}

interface FullColor {
	hex: string
}

export default function Background(props: Props) {
	const {
		meeting,
		user,
		token,
	} = useSelector((state: GreenroomState) => state.SessionReducer);
	const defaultBackgrounds = useSelector(
		(state: GreenroomState) => state.ContentReducer.defaultBackgrounds,
	);

	const { queue } = props;
	const [fullColor, setFullColor] = useState<FullColor>({ hex: queue.backgroundColor });
	const [showColorPicker, setShowColorPicker] = useState(false);
	const [showBackgroundImagePicker, setShowBackgroundImagePicker] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [backgrounds, setBackgrounds] = useState<UserMedia[]>([]);
	const fileInput = useRef<HTMLInputElement | null>(null);
	const [scale, setScale] = useState(queue.backgroundScale);
	const [selectedBackground, setSelectedBackground] = useState<string>('');
	const scaleDebounce = useRef<NodeJS.Timeout | null>(null);
	const colorDebounce = useRef<NodeJS.Timeout | null>(null);

	function getBackgrounds(): undefined {
		if (!token || !user || !meeting) {
			return;
		}

		GetUserMediaCanvasBackgrounds(token, meeting.meeting).then((response: UserMedia[]) => {
			setBackgrounds(response);
		});
	}

	useEffect(() => {
		getBackgrounds();
	}, []);

	useEffect(() => {
		getBackgrounds();
	}, [user, token, meeting]);

	useEffect(() => {
		if (props.appliedMatchesQueue) {
			setFullColor({ hex: queue.backgroundColor });
			setScale(queue.backgroundScale);
		}
	}, [props.appliedMatchesQueue]);

	function toggleColorPicker() {
		setShowColorPicker(!showColorPicker);
	}

	function closeColorPicker() {
		setShowColorPicker(false);
	}

	function closeBackgroundImagePicker() {
		setShowBackgroundImagePicker(false);
	}

	function setBGImage(url: string) {
		const newQueue = { ...queue, backgroundType: 'image', backgroundImage: url };
		props.settingsForShotEdited(newQueue);
		setSelectedBackground(url);
	}

	async function checkFileInput(event: React.ChangeEvent<HTMLInputElement>) {
		if (!token || !user || !meeting) {
			return;
		}

		const newImage = event.target.files?.[0];
		if (!newImage) return;
		
		const extension = getFileExtension(newImage?.name ?? '');
		const resizedImg = await resizeImage(newImage, extension, 1920, 1080);
		if (!resizedImg) return;

		setUploading(true);

		const url = await UploadImage(token, resizedImg)
			.then((res: UploadResponse) => res.url)
			.catch(() => '');
			
		setUploading(false);
		
		if (!url) return;

		setBGImage(url);

		await UploadUserMediaCanvasBackground(token, user.gr_user, meeting.meeting, url);

		getBackgrounds();
	}

	function clickFileInput() {
		fileInput.current?.click();
	}

	const backgroundStyle = useMemo(() => {
		const style: any = {
			backgroundColor: '#000',
			backgroundPosition: 'center',
		};

		if (queue.backgroundImage) {
			style.backgroundImage = `url("${getFullAssetURI(queue.backgroundImage)}")`;
			style.backgroundSize = `${scale}%`;
			style.backgroundColor = fullColor.hex;
		}

		return style;
	}, [fullColor.hex, queue.backgroundImage, scale]);

	function handleScrub(e: React.ChangeEvent<HTMLInputElement>): void {
		const newScale = Number(e.target.value);
		setScale(newScale);

		if (scaleDebounce.current) { clearTimeout(scaleDebounce.current); }

		scaleDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, backgroundScale: newScale };
			props.settingsForShotEdited(newQueue);
		}, 100);
	}

	function colorChange(color: ColorResult) {
		setFullColor(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, backgroundColor: color.hex };
			props.settingsForShotEdited(newQueue);
		}, 100);
	}

	function clearBgImage() {
		const newQueue = { ...queue, backgroundType: 'color' };
		props.settingsForShotEdited(newQueue);
		setSelectedBackground('');
	}

	function deleteImage(image: ImagePickerMedia) {
		if (!token) {
			return;
		}

		DeleteUserMedia(token, image.id).then(getBackgrounds);
	}

	async function openImagePicker() {
		try {
			await getBackgrounds();
		} finally {
			setShowBackgroundImagePicker(true);
		}
	}

	const channelLogo: string = useMemo(() => getChannelLogoFromMeeting(meeting), [meeting]);

	return (
		<>
			<div className="settings-container background-container">
				<label className="section-title margin-bottom-medium">
					<span className={classNames('title-text')}>Background</span>
				</label>
				<div className="background-fill-options">
					<div className="background-image-container">
						{queue.backgroundType === 'image'
							? (<div className="background-image" style={backgroundStyle} onClick={openImagePicker} />)
							: (<div className="color-background" style={{ backgroundColor: fullColor.hex }} onClick={() => setShowBackgroundImagePicker(true)}>Color Only</div>)}
					</div>

					<div className="background-image-select">
						<div className="display-flex width-full margin-bottom-24">
							<span className="text-dark-gray margin-right-small">Background Fill</span>
							<InfoBadge message={InfoBadgeMsg.Background} />
						</div>
						<button className="image-select round padding-small background-lighter text-white width-full" onClick={clickFileInput} disabled={uploading} >
							{uploading
								? <WaitingIndicator />
								: <><Icon name={ICONS.ADD} size={14} color={COLORS.WHITE} className='margin-right-small' /><span>Add</span></>
							}
						</button>
					</div>

				</div>
				<input
					accept={IMG_TYPES.map((a) => `.${a}`).join(',')}
					onChange={checkFileInput}
					ref={fileInput}
					type="file"
					style={{ display: 'none' }}
				/>

				<ImagePicker
					selectedImage={selectedBackground}
					backgroundType={queue.backgroundType}
					logo={channelLogo}
					thumbnail={meeting?.thumbnail ?? ''}
					label={'Background Image'}
					close={closeBackgroundImagePicker}
					open={showBackgroundImagePicker}
					setImage={setBGImage}
					clearBgImage={clearBgImage}
					externalImages={backgrounds}
					deleteImage={deleteImage}
					defaultBackgrounds={defaultBackgrounds}
				/>
				
				<ColorPicker
					open={showColorPicker}
					openHexAtStart={fullColor.hex}
					portalId='color-portal-background'
					onClose={() => setShowColorPicker(false)}
					onChange={colorChange}
					customPosition={{top:-269.5,right:-1}}
				/>

				<div className={`background-scale ${queue.backgroundType === 'image' ? '' : 'disabled'}`}>
					<label>Scale</label>
					<input onChange={handleScrub} value={scale} type="range" min="20" max="250" disabled={queue.backgroundType !== 'image'}/>
					<span >{scale}%</span>
				</div>

				<div className="background-color-container">
					<label>Background color</label>
					<div className={classNames('color-toggle', { editing: showColorPicker })} onClick={toggleColorPicker}>
						<div className="color-swatch" style={{ backgroundColor: fullColor.hex }}></div>
						<span className='format-hex'>{fullColor.hex}</span>
					</div>
					<div id='color-portal-background' className='color-portal portal-right-aligned'/>
				</div>
			</div>
		</>
	);
}
